import { useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import {
  ArrowLeft,
  Bot,
  icons,
  Keyboard,
  LayoutGrid,
  Pencil,
  Plus,
  Star,
  Tag,
  Trash2,
} from "lucide-react";
import { useMemo } from "react";
import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { Separator } from "~/components/ui/separator";
import { $deleteTemplate, $toggleTemplateFavorite } from "~/lib/mutations";
import { $getPromptTemplate } from "~/lib/queries";

export const Route = createFileRoute(
  "/(authenticated)/dashboard/settings/templates/$templateId/",
)({
  component: ViewTemplatePage,
});

function getIconName(name: string | null): keyof typeof icons {
  if (!name) return "Zap";
  const pascalName = name
    .split("-")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join("");
  return (pascalName in icons ? pascalName : "Zap") as keyof typeof icons;
}

const colorMap: Record<string, { bg: string; text: string }> = {
  blue: {
    bg: "bg-blue-100 dark:bg-blue-900/30",
    text: "text-blue-600 dark:text-blue-400",
  },
  green: {
    bg: "bg-green-100 dark:bg-green-900/30",
    text: "text-green-600 dark:text-green-400",
  },
  purple: {
    bg: "bg-purple-100 dark:bg-purple-900/30",
    text: "text-purple-600 dark:text-purple-400",
  },
  orange: {
    bg: "bg-orange-100 dark:bg-orange-900/30",
    text: "text-orange-600 dark:text-orange-400",
  },
  red: { bg: "bg-red-100 dark:bg-red-900/30", text: "text-red-600 dark:text-red-400" },
  pink: {
    bg: "bg-pink-100 dark:bg-pink-900/30",
    text: "text-pink-600 dark:text-pink-400",
  },
  yellow: {
    bg: "bg-yellow-100 dark:bg-yellow-900/30",
    text: "text-yellow-600 dark:text-yellow-400",
  },
  cyan: {
    bg: "bg-cyan-100 dark:bg-cyan-900/30",
    text: "text-cyan-600 dark:text-cyan-400",
  },
};

function ViewTemplatePage() {
  const { templateId } = Route.useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: template } = useSuspenseQuery({
    queryKey: ["prompt-template", templateId],
    queryFn: () => $getPromptTemplate({ data: { id: templateId } }),
  });

  const IconComponent = useMemo(() => icons[getIconName(template.icon)], [template.icon]);
  const colors = colorMap[template.color ?? "blue"] ?? colorMap.blue;

  const handleToggleFavorite = async () => {
    await $toggleTemplateFavorite({ data: { id: template.id } });
    queryClient.invalidateQueries({ queryKey: ["prompt-template", templateId] });
    queryClient.invalidateQueries({ queryKey: ["prompt-templates"] });
  };

  const handleDelete = async () => {
    if (confirm("Are you sure you want to delete this template?")) {
      await $deleteTemplate({ data: { id: template.id } });
      queryClient.invalidateQueries({ queryKey: ["prompt-templates"] });
      navigate({ to: "/dashboard/settings/templates" });
    }
  };

  const handleUseTemplate = () => {
    navigate({
      to: "/dashboard/settings/templates",
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button
          variant="outline"
          size="icon"
          onClick={() => navigate({ to: "/dashboard/settings/templates" })}
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div
          className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl ${colors.bg} ${colors.text}`}
        >
          <IconComponent className="h-6 w-6" />
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h1 className="truncate text-xl font-bold">{template.name}</h1>
            {template.isFavorite && (
              <Star className="h-4 w-4 shrink-0 fill-yellow-400 text-yellow-400" />
            )}
          </div>
          {template.description && (
            <p className="text-muted-foreground line-clamp-1 text-sm">
              {template.description}
            </p>
          )}
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <Button onClick={handleUseTemplate} size="sm" className="gap-1.5">
          <Plus className="h-4 w-4" />
          Use Template
        </Button>
        <Button
          variant="outline"
          size="sm"
          render={(props) => (
            <Link
              {...props}
              to="/dashboard/settings/templates/$templateId/edit"
              params={{ templateId: template.id }}
            >
              <Pencil className="h-4 w-4" />
            </Link>
          )}
        />
        <Button
          variant="outline"
          size="sm"
          onClick={handleToggleFavorite}
          className={
            template.isFavorite
              ? "border-yellow-200 bg-yellow-50 dark:border-yellow-900/30 dark:bg-yellow-900/20"
              : ""
          }
        >
          <Star
            className={`h-4 w-4 ${template.isFavorite ? "fill-yellow-400 text-yellow-400" : ""}`}
          />
        </Button>
        <Button variant="outline" size="sm" onClick={handleDelete}>
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>

      <Separator />

      <div className="flex flex-wrap items-center gap-4 text-sm">
        {template.keyboardShortcut && (
          <div className="text-muted-foreground flex items-center gap-1.5">
            <Keyboard className="h-4 w-4" />
            <kbd className="bg-muted rounded px-1.5 py-0.5 font-mono text-xs">
              {template.keyboardShortcut}
            </kbd>
          </div>
        )}
        <div className="text-muted-foreground flex items-center gap-1.5">
          <Bot className="h-4 w-4" />
          <span>{template.defaultModel}</span>
        </div>
        {template.defaultProvider && (
          <div className="text-muted-foreground flex items-center gap-1.5">
            <LayoutGrid className="h-4 w-4" />
            <span>{template.defaultProvider.displayName}</span>
          </div>
        )}
      </div>

      {template.tags && template.tags.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <Tag className="text-muted-foreground h-4 w-4" />
          {template.tags.map((tag: string) => (
            <span
              key={tag}
              className="bg-muted text-muted-foreground rounded-md px-2 py-0.5 text-xs"
            >
              {tag}
            </span>
          ))}
        </div>
      )}

      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">System Prompt</CardTitle>
          </CardHeader>
          <CardContent>
            {template.systemPrompt ? (
              <pre className="bg-muted rounded-lg p-4 text-sm whitespace-pre-wrap">
                {template.systemPrompt}
              </pre>
            ) : (
              <p className="text-muted-foreground text-sm italic">No system prompt set</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">User Prompt Prefix</CardTitle>
          </CardHeader>
          <CardContent>
            {template.userPromptPrefix ? (
              <pre className="bg-muted rounded-lg p-4 text-sm whitespace-pre-wrap">
                {template.userPromptPrefix}
              </pre>
            ) : (
              <p className="text-muted-foreground text-sm italic">
                No user prompt prefix set
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Usage Statistics</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <p className="text-muted-foreground text-sm">Times Used</p>
              <p className="text-2xl font-bold">{template.usageCount}</p>
            </div>
            <div>
              <p className="text-muted-foreground text-sm">Created</p>
              <p className="font-medium">
                {new Date(template.createdAt).toLocaleDateString()}
              </p>
            </div>
            <div>
              <p className="text-muted-foreground text-sm">Last Updated</p>
              <p className="font-medium">
                {new Date(template.updatedAt).toLocaleDateString()}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
